"use client";

import { cn } from "@/lib/utils";
import {
  ROMA_BRANCHES,
  resolveRomaBranchCopy,
  romaBranchLabel,
  type RomaBranch,
} from "@/lib/roma-branch";

type RomaBranchChoiceProps = {
  name?: string;
  value: "" | RomaBranch;
  onChange: (value: RomaBranch) => void;
  legend: string;
  intro?: string;
  error?: string;
  className?: string;
};

export function RomaBranchChoice({
  name = "romaBranch",
  value,
  onChange,
  legend,
  intro,
  error,
  className,
}: RomaBranchChoiceProps) {
  return (
    <fieldset
      className={cn("space-y-3", className)}
      aria-describedby={error ? `${name}-error` : undefined}
    >
      <legend className="roma-eyebrow text-[var(--roma-terracotta,#f45c24)]">
        {legend}
      </legend>
      {intro ? (
        <p className="text-sm text-[var(--roma-espresso,#2b1c17)]/72">
          {intro}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {ROMA_BRANCHES.map((branch) => {
          const copy = resolveRomaBranchCopy(branch);
          const checked = value === branch;
          return (
            <label
              key={branch}
              className={cn(
                "flex cursor-pointer gap-3 rounded-[12px] border p-4 transition-colors",
                checked
                  ? "border-[var(--roma-terracotta,#f45c24)] bg-[var(--roma-ivory,#fffaf0)]"
                  : "border-[var(--roma-parchment-line,#e3e3e3)] bg-white",
              )}
            >
              <input
                type="radio"
                name={name}
                value={branch}
                checked={checked}
                required
                onChange={() => onChange(branch)}
                className="mt-1 h-4 w-4 accent-[var(--roma-terracotta,#f45c24)]"
              />
              <span className="min-w-0">
                <span className="block font-semibold text-[var(--roma-espresso,#2b1c17)]">
                  {romaBranchLabel(branch)}
                </span>
                {copy ? (
                  <span className="mt-0.5 block text-sm text-[var(--roma-espresso,#2b1c17)]/72">
                    {copy.addressLine1}
                    <br />
                    {copy.addressLine2}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {error ? (
        <p id={`${name}-error`} role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
